// Lib
import React from 'react';
import { useSelector } from 'react-redux';

// Utils
import { pluralise } from '../../../common/utils/stringUtil';
import { getFileSizeComparisonParts } from '../../../common/files/fileSizeUtils';

// Selectors
import {
    allDownloadsSelector,
    allUploadsSelector,
    hasAttachmentsUploads,
    selectIsUploadingAttachmentWithProgress,
} from './attachmentsSelector';

// Hooks
import useStalledBoolean from '../../utils/react/useStalledBoolean';

// Components
import Toast from '../../structural/toast/Toast';
import Spinner from '../../components/loaders/Spinner';
import ProgressBar from '../file/uploader/ProgressBar';
import AnimationLock from '../../components/animations/AnimationLock';

// Constants
import { ELEMENT_ATTACHMENT_ACTION_TYPES } from '../../../common/elements/elementConstants';

// Styles
import './FileTransferToast.scss';

const ProgressSize: React.FC<{ loaded: number; total: number }> = ({ loaded, total }) => {
    const { suffix, totalFormatted, partialFormatted } = getFileSizeComparisonParts(loaded, total);
    return (
        <div className="progress-size">
            <span className="partial">{partialFormatted}</span>
            <span className="slash">/</span>
            <span className="total">{totalFormatted}</span>
            <span className="unit"> {suffix}</span>
        </div>
    );
};

// Structured as a subcomponent of the Toast to ensure it updates when the store changes
const FileTransferToastContent: React.FC = () => {
    const isUploadingAttachmentWithProgress = useSelector(selectIsUploadingAttachmentWithProgress);

    const uploadData = useSelector(allUploadsSelector);
    const downloadData = useSelector(allDownloadsSelector);
    const hasUploads = useSelector(hasAttachmentsUploads);

    const progress = hasUploads ? uploadData : downloadData;
    const { count, total, loaded, showProgress, actionType } = progress;

    const textMessage = actionType === ELEMENT_ATTACHMENT_ACTION_TYPES.DOWNLOAD ? 'Downloading' : 'Uploading';

    // Don't update the toast contents if the component is getting removed
    return (
        <AnimationLock animating={!isUploadingAttachmentWithProgress}>
            {showProgress ? <ProgressBar percentageComplete={(loaded / total) * 100} /> : <Spinner show />}
            <span className="uploading-message">
                {textMessage} {count} {pluralise('file', count)}&nbsp;
            </span>
            {showProgress && <ProgressSize loaded={loaded} total={total} />}
        </AnimationLock>
    );
};

const FileTransferToast: React.FC = (props) => {
    const isUploadingAttachmentWithProgress = useSelector(selectIsUploadingAttachmentWithProgress);
    // Ensure the toast shows for 1 second after the upload completes - so that we can show the
    //  "completed" message
    const showToast = useStalledBoolean(isUploadingAttachmentWithProgress, 0, 1000);

    if (!showToast) return null;

    return (
        <Toast className="FileTransferToast">
            <FileTransferToastContent />
        </Toast>
    );
};

export default FileTransferToast;
